import { Gauge } from "lucide-react";

import { cn } from "@/lib/utils";
import type { AccuracyLevel, RunAccuracy } from "@/types/api";

// A run's accuracy (AVI-170): the automatic score from the free signals and the
// judge, or an evaluator's thumbs up / down when there is one. Only answers built
// on tool or knowledge-base data are scored; the judge also gives a % accurate,
// its reason and a suggested fix.

const LEVEL: Record<AccuracyLevel, { label: string; cls: string; mark: string }> = {
  high: {
    label: "High",
    cls: "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]",
    mark: "●",
  },
  medium: {
    label: "Medium",
    cls: "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))]",
    mark: "▲",
  },
  // Not `text-destructive`: its dark-theme step is too dark to read as text.
  low: { label: "Low", cls: "bg-red-500/15 text-red-500", mark: "■" },
};

/** Accuracy as colour, a shape and a word, never colour alone. */
export function AccuracyChip({
  level,
  pct,
  className,
}: {
  level: AccuracyLevel;
  /** The judge's % accurate, shown after the word. */
  pct?: number | null;
  className?: string;
}) {
  const s = LEVEL[level];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium",
        s.cls,
        className,
      )}
    >
      <span aria-hidden="true" className="text-[10px]">
        {s.mark}
      </span>
      {s.label}
      {pct != null ? <span className="tabular-nums">· {pct}%</span> : null}
    </span>
  );
}

/** The judge's reason and suggested fix, in words. */
export function JudgeNotes({
  explanation,
  fix,
}: {
  explanation: string | null;
  fix: string | null;
}) {
  if (!explanation && !fix) return null;
  return (
    <div className="grid gap-0.5">
      {explanation ? <p className="whitespace-pre-wrap">{explanation}</p> : null}
      {fix ? (
        <p className="whitespace-pre-wrap">
          <span className="font-medium">Suggested fix: </span>
          {fix}
        </p>
      ) : null}
    </div>
  );
}

const grade = (v: number | null) => (v == null ? null : `${Math.round(v * 100)}%`);

/** The signals behind a run's accuracy, in words. */
export function AccuracyVerdict({ accuracy: a }: { accuracy: RunAccuracy }) {
  const signals = [
    a.valid_query == null
      ? null
      : a.valid_query
        ? "SQL ran and returned rows"
        : "SQL failed or returned no rows",
    a.retrieval_score == null ? null : `KB match ${a.retrieval_score.toFixed(2)}`,
    a.relevance == null ? null : `relevance ${grade(a.relevance)}`,
    a.grounded == null ? null : `grounded ${grade(a.grounded)}`,
  ].filter(Boolean);
  const automatic = a.source === "automatic" || a.automatic_overall != null;
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2 font-medium">
        <Gauge className="h-4 w-4" />
        Accuracy
        <AccuracyChip level={a.overall} pct={a.source === "automatic" ? a.accuracy_pct : null} />
        <span className="text-xs font-normal text-muted-foreground">
          {a.source === "evaluator"
            ? `set by an evaluator's rating${
                a.automatic_overall
                  ? ` · automatic score was ${LEVEL[a.automatic_overall].label}${
                      a.accuracy_pct != null ? ` (${a.accuracy_pct}% accurate)` : ""
                    }`
                  : ""
              }`
            : "automatic"}
        </span>
      </div>
      {automatic ? (
        <div className="mt-1 text-xs text-muted-foreground">
          {signals.length ? signals.join(" · ") : "No signals recorded."}
          {a.judge_skipped
            ? " · judge skipped: the knowledge-base match was too weak to support an answer."
            : a.judge_model
              ? ` · judged by ${a.judge_model}`
              : ""}
          <div className="mt-1 text-foreground">
            <JudgeNotes
              explanation={a.source === "automatic" ? a.explanation : a.automatic_explanation}
              fix={a.suggested_fix}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
